<script lang="ts" setup>
import { ref, Ref, computed } from "vue";
//@ts-ignore
import FormInput from "@/components/form/input.vue";
//@ts-ignore
import FormCombobox from "@/components/form/combobox.vue";
//@ts-ignore
import FormCheckbox from "@/components/form/checkbox.vue";
//@ts-ignore
import Submit from "@/components/form/submit.vue";
import * as Yup from "yup";
import { nl } from "yup-locales";
import { useErrors } from "@/utils/api-functions";
import { useApiStore, type WpProject, type WhiseUnit } from "@/stores/api";
//@ts-ignore
import EstateCard from "@/components/elements/whise-estate-card.vue";
//@ts-ignore
import ProjectCard from "@/components/elements/project-card.vue";
import { useForm } from "vee-validate";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/solid";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

const apiStore = useApiStore();
const apiErrors = useErrors();

apiStore.getCities().then((c: string[]) => {
    cities.value = c.map((c) => {
        return { id: c, name: c };
    });
});

Yup.setLocale(nl);

const projects: Ref<WpProject[]> = ref([]);
const units: Ref<WhiseUnit[]> = ref([]);
const cities: Ref<Array<{ id: string | number; name: string }>> = ref([]);
const bedrooms = [
    { id: 1, name: "1+" },
    { id: 2, name: "2+" },
    { id: 3, name: "3+" },
    { id: 4, name: "4+" },
    { id: 5, name: "5+" },
];
const bathrooms = [
    { id: 1, name: "1+" },
    { id: 2, name: "2+" },
    { id: 3, name: "3+" },
    { id: 4, name: "4+" },
    { id: 5, name: "5+" },
];
const success = ref(false);
const loading = ref(true);
const currentPage = ref(0);
const total = ref(0);
const getData = (params: { bedrooms?: string; bathrooms?: string; for_sale?: boolean; for_rent?: boolean; city?: string[] } = {}) => {
    const promises = [];

    promises.push(apiStore.getWpProjects({ page: currentPage.value, per_page: 2, categories_exclude: [3], ...params }).then((e: WpProject[]) => (projects.value = e)));
    promises.push(
        apiStore.getWhiseUnits({ page: currentPage.value, per_page: 12, ...params }).then((e: any) => {
            units.value = e.data;
            total.value = e.total;
            currentPage.value = e.page;
        }),
    );

    Promise.all(promises).then(() => {
        loading.value = false;
        success.value = true;
    });
};
const componentKey = ref(0);
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = computed(() => breakpoints.isSmaller("lg"));

const getUrlQueryParameter = (name: string, fallback: any) => {
    const params = new URLSearchParams(window.location.search);

    if (params.has(name)) {
        return params.get(name);
    }

    return fallback;
};

const { handleSubmit, isSubmitting, resetForm, meta, values } = useForm({
    initialValues: {
        bedrooms: undefined,
        bathrooms: undefined,
        status: [],
        price_min: undefined,
        price_max: undefined,
        type: [],
        city: getUrlQueryParameter("city", "").split(",") || [],
    },
    validationSchema: Yup.object({
        bedrooms: Yup.string().optional(),
        bathrooms: Yup.string().optional(),
        city: Yup.array().optional(),
        status: Yup.array().optional(),
        type: Yup.array().optional(),
        price_min: Yup.number().optional(),
        price_max: Yup.number().optional(),
    }),
});

const open = ref(false);
const onSubmit = handleSubmit(async (values) => {
    try {
        success.value = false;
        loading.value = true;
        currentPage.value = 0;

        getData(JSON.parse(JSON.stringify(values)));

        updateUrlQueryParameters({ city: values.city });

        success.value = true;
        open.value = false;
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

const handleReset = async () => {
    await resetForm();

    componentKey.value += 1;
    currentPage.value = 0;
    getData();
};

const setPage = (page: number) => {
    currentPage.value = page;
    getData(JSON.parse(JSON.stringify(values)));
};

const updateUrlQueryParameters = (payload: { city?: any }) => {
    const params = new URLSearchParams(window.location.search);

    // clear all params
    params.delete("city");

    if (payload.city && payload.city.length > 0) {
        params.set("city", payload.city.join(","));
    }

    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
};

const zeroToThree = computed(() => units.value.slice(0, isMobile.value ? 4 : 3));
const ThreeToNine = computed(() => units.value.slice(isMobile.value ? 4 : 3, isMobile.value ? 10 : 9));
const NineToTwelve = computed(() => units.value.slice(isMobile.value ? 10 : 9, isMobile.value ? 13 : 12));
const firstProject = computed(() => (ThreeToNine.value.length > 0 ? projects.value[0] : null));
const secondProject = computed(() => (NineToTwelve.value.length > 0 ? projects.value[1] : null));

/** Wrapping up  */
getData({
    city: getUrlQueryParameter("city", "").split(",") || [],
});
</script>

<template>
    <div class="container">
        <div class="fixed w-full lg:relative lg:mb-[70px] bg-white z-[200] lg:z-10 left-0 h-[100vh] lg:!h-[auto] transition-all lg:!top-0" :class="{ 'top-0': open, 'top-auto bottom-offset': !open }">
            <div class="bg-gold text-white left-0 bottom-0 w-full container py-[12.5px] tracking-[0.09px] text-14 leading-[24px] font-bold lg:hidden flex justify-between items-center" @click="open = !open">
                <span>{{ !open ? "Open filters" : "Sluit filters" }}</span>
                <ChevronDownIcon v-if="open" class="w-[20px] h-[20px] text-white"></ChevronDownIcon>
                <ChevronUpIcon v-else="open" class="w-[20px] h-[20px] text-white"></ChevronUpIcon>
            </div>
            <form class="lg:mb-[70px] h-full lg:h-auto px-[36px] lg:px-0 pt-[36px] pb-[72px] lg:pt-0 lg:pb-0 overflow-auto lg:overflow-hidden" @submit="onSubmit">
                <div class="lg:hidden text-14 font-light leading-[24px] tracking-[0.09px] mb-[32px]">Vul de filters hieronder in, en wie weet vind je wel wat je zoekt.</div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-[40px] items-start gap-y-[16px] lg:gap-y-0">
                    <div class="grid gap-y-[8px] items-start">
                        <div class="flex flex-col lg:flex-row gap-x-[29px] xl:justify-between">
                            <span class="text-14 font-light flex-none flex flex-row items-center">
                                <fa-icon icon="location-dot" class="text-15 text-brown-200 mr-[4px]" />
                                Locatie
                            </span>

                            <div class="lg:w-[377px] xl:w-[442px] flex flex-row items-center flex-none">
                                <form-combobox name="city" :options="cities" class="w-full"></form-combobox>
                            </div>
                        </div>
                        <div class="flex flex-col lg:flex-row gap-x-[29px] items-center mb-[10px] lg:mb-0">
                            <span class="text-14 font-light flex-none w-full lg:w-[70px] flex flex-row items-center">
                                <fa-icon icon="money-bill" class="text-15 text-brown-200 mr-[4px]" />
                                Prijs
                            </span>
                            <div class="flex flex-col lg:flex-row items-center gap-x-[16px] w-full lg:w-auto mb-[10px] lg:mb-0">
                                <span class="hidden xl:block text-14 font-light w-full lg:w-[65px] flex-none">Minimum</span>
                                <span class="hidden lg:block xl:hidden text-14 font-light w-full lg:w-[35px] flex-none">Max</span>
                                <div class="relative w-full lg:w-auto mb-[8px] lg:mb-[0]">
                                    <span class="absolute left-[6px] leading-[44px] text-brown-200 font-light text-14">€</span>
                                    <form-input name="price_min" type="number" placeholder="Min." class="price-input w-full lg:w-[89px] flex-none"></form-input>
                                </div>
                                <div class="relative w-full lg:w-auto lg:hidden">
                                    <span class="absolute left-[6px] leading-[44px] text-brown-200 font-light text-14">€</span>
                                    <form-input name="price_max" type="number" placeholder="Max." class="price-input w-full lg:w-[89px] flex-none lg:placeholder:opacity-0"></form-input>
                                </div>
                            </div>
                            <div class="flex flex-col lg:flex-row items-center gap-x-[16px] w-full lg:justify-between">
                                <span class="text-14 font-light w-full lg:w-[120px] xl:w-[150px] flex-none justify-between">
                                    <fa-icon icon="bed" class="text-15 text-brown-200 mr-[4px]" />
                                    Slaapkamers
                                </span>
                                <form-combobox name="bedrooms" mode="single" :options="bedrooms" class="w-full lg:w-[68px] flex-none"></form-combobox>
                            </div>
                        </div>
                        <div class="flex flex-col lg:flex-row gap-x-[29px] items-center">
                            <span class="text-14 font-light flex-none lg:w-[70px] flex flex-row items-center"></span>
                            <div class="hidden lg:flex flex-col lg:flex-row items-center gap-x-[16px]">
                                <span class="hidden xl:block text-14 font-light w-full lg:w-[65px] flex-none">Maximum</span>
                                <span class="hidden lg:block xl:hidden text-14 font-light w-full lg:w-[35px] flex-none">Max</span>
                                <div class="relative">
                                    <span class="absolute left-[6px] leading-[44px] text-brown-200 font-light text-14">€</span>
                                    <form-input name="price_max" type="number" class="price-input w-[89px] flex-none"></form-input>
                                </div>
                            </div>
                            <div class="flex flex-col lg:flex-row items-center gap-x-[16px] w-full lg:justify-between">
                                <span class="text-14 font-light w-full lg:w-[120px] xl:w-[150px] flex-none">
                                    <fa-icon icon="bath" class="text-15 text-brown-200 mr-[4px] mb-[10px] lg:mb-0" />
                                    Badkamers
                                </span>
                                <form-combobox name="bathrooms" mode="single" :options="bathrooms" class="w-full lg:w-[68px] flex-none"></form-combobox>
                            </div>
                        </div>
                    </div>
                    <div class="grid gap-y-[8px]">
                        <div class="grid grid-cols-1 lg:grid-cols-2 lg:h-[44px]">
                            <form-checkbox :key="componentKey" name="status" class="md flex mb-15 lg:mb-0 w-full lg:w-auto items-center" value="sale">
                                <span class="text-15 text-brown-200 font-light">Te koop</span>
                            </form-checkbox>
                            <form-checkbox :key="componentKey" name="status" class="md flex mb-15 lg:mb-0 w-full lg:w-auto items-center" value="rent">
                                <span class="text-15 text-brown-200 font-light">Te huur</span>
                            </form-checkbox>
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 lg:h-[44px]">
                            <form-checkbox :key="componentKey" name="type" class="md flex mb-15 lg:mb-0 w-full lg:w-auto items-center" value="huis">
                                <fa-icon icon="house-chimney" class="text-15 text-brown-200 mr-[8px]" />
                                <span class="text-15 text-brown-200 font-light">Huis</span>
                            </form-checkbox>
                            <form-checkbox :key="componentKey" name="type" class="md flex mb-15 lg:mb-0 w-full lg:w-auto items-center" value="appartement">
                                <fa-icon icon="building" class="text-15 text-brown-200 mr-[8px]" />
                                <span class="text-15 text-brown-200 font-light">Appartement</span>
                            </form-checkbox>
                        </div>
                        <div class="grid grid-cols-1 lg:grid-cols-2 lg:h-[44px] mb-[10px] lg:mb-0">
                            <form-checkbox :key="componentKey" name="type" class="md flex mb-15 lg:mb-0 w-full lg:w-auto items-center" value="commercieel">
                                <fa-icon icon="shop" class="text-15 text-brown-200 mr-[8px]" />
                                <span class="text-15 text-brown-200 font-light">Handelspand</span>
                            </form-checkbox>

                            <form-checkbox :key="componentKey" name="type" class="md flex mb-15 lg:mb-0 w-full lg:w-auto items-center" value="parking">
                                <fa-icon icon="square-parking" class="text-15 text-brown-200 mr-[8px]" />
                                <span class="text-15 text-brown-200 font-light">Parking/Garage</span>
                            </form-checkbox>
                        </div>
                        <div class="flex flex-col lg:flex-row gap-[8px] justify-end">
                            <submit :loading="isSubmitting || loading" class="!bg-brown-100 !text-white hover:!bg-beige-300 hover:!text-brown-100 block">Zoeken</submit>

                            <button
                                :class="{ 'opacity-50 pointer-events-none': !meta.dirty }"
                                type="button"
                                class="block text-brown-200 font-helv font-bold text-16 leading-[24px] border border-brown-200 hover:border-beige-200 hover:bg-beige-200 transition-colors h-[48px] px-[48px]"
                                @click="handleReset"
                            >
                                Wissen
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-x-[32px] gap-y-[35px] lg:gap-y-0 mb-[51px]">
            <estate-card class="lg:even:bg-beige-300" v-for="estate in zeroToThree" :key="estate.id" :estate="estate" data-aos="fade-up"></estate-card>
        </div>

        <div v-if="zeroToThree.length === 0 && !loading && !firstProject" class="text-center">Geen resultaten gevonden</div>
    </div>

    <project-card class="mb-[35px] lg:mb-[102px]" v-if="firstProject" :estate="firstProject" :key="firstProject.id"></project-card>

    <div class="container">
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-x-[32px] gap-y-[35px] lg:gap-y-[51px] mb-[51px]">
            <estate-card class="lg:even:bg-beige-300" v-for="estate in ThreeToNine" :key="estate.id" :estate="estate"></estate-card>
        </div>
    </div>

    <project-card class="mb-[35px] lg:mb-[102px]" v-if="secondProject" :estate="secondProject" :key="secondProject.id"></project-card>

    <div class="container">
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-x-[32px] gap-y-[35px] lg:gap-y-0 mb-[51px]">
            <estate-card class="lg:even:bg-beige-300" v-for="estate in NineToTwelve" :key="estate.id" :estate="estate"></estate-card>
        </div>

        <div v-if="total > 12" class="flex items-center justify-center relative z-20">
            <div class="relative z-50 flex flex-wrap gap-[4px] pb-[32px]">
                <a
                    v-for="page in Math.ceil(total / 12)"
                    @click="setPage(page - 1)"
                    href="#estates"
                    class="bg-beige-200 w-[32px] h-[32px] font-medium flex items-center justify-center cursor-pointer hover:bg-beige-300 transition-colors"
                    :class="{ '!bg-brown-100 text-white': page - 1 === currentPage }"
                >
                    {{ page }}
                </a>
            </div>
        </div>
    </div>
</template>

<style scoped>
.bottom-offset {
    top: calc(var(--vh, 1vh) * 100 - 49px);
}
:deep input::-webkit-outer-spin-button,
:deep input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
:deep input[type="number"] {
    -moz-appearance: textfield;
}
:deep .price-input input {
    @apply border-none focus:border-none text-brown-200 text-14 leading-[20px] tracking-[0px] font-light pl-[20px] pr-[10px] py-[0] relative lg:placeholder:text-none;
    background: #09132110;
    height: 44px;
}
.checkbox {
    background: #09132110;
    height: 30px;
    width: 30px;
}
</style>
